import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { service } from '@ember/service';
import { array } from '@ember/helper';
import { on } from '@ember/modifier';
import { formatNumber } from 'ember-intl';
import { and, not, notEq, or } from 'ember-truth-helpers';
import HintedSummary from './hinted-summary.gts';
import PlatformBadge from './platform-badge';
import SourceProcessing from './source-processing.gts';
import imageFadeIn from '../modifiers/image-fade-in';
import type { ContentSource } from '../schemas/content-source.ts';
import type { Mention } from '../schemas/mention.ts';
import type { Summary } from '../schemas/summary.ts';
import type Language from '../services/language';
import type Lightbox from '../services/lightbox';
import { formatDuration } from '../utils/format';
import Icon from './icon';
import AlertCircleIcon from '~icons/lucide/alert-circle';
import { toApiUrl } from '../fetch';

const PROCESSING_STATES = new Set([
  'pending',
  'downloading',
  'transcribing',
  'analyzing',
  'enriching',
]);

// Persists across component lifecycle within the SPA session so that
// router.refresh() recreating this component doesn't remount SourceProcessing.
const completedSourceIds = new Set<string>();

interface SourceDetailsHeaderSignature {
  Args: {
    source: ContentSource;
    mentions?: Mention[];
    mentionId?: string;
    clearCache?: () => void;
    refresh?: () => unknown;
  };
  Element: HTMLDivElement;
}

/**
 * Component that displays the content source header including:
 * - Thumbnail, title, platform, language, duration
 * - AI Summary with topics
 * - Stats (views, likes, comments)
 * - Admin actions (flag, admin menu)
 *
 * Tracks local `hasCompleted` state to prevent the SourceProcessing component
 * from re-mounting after extraction completes (which would cause the SSE
 * connection to re-open and fire the "Extraction Complete" notification again
 * while WarpDrive refreshes the stale cached source record).
 */
export default class SourceDetailsHeader extends Component<SourceDetailsHeaderSignature> {
  @service declare language: Language;
  @service declare lightbox: Lightbox;

  @tracked hasCompleted = completedSourceIds.has(this.args.source.id ?? '');

  @action
  handleCompleted() {
    completedSourceIds.add(this.args.source.id ?? '');
    this.hasCompleted = true;
    this.args.clearCache?.();
    void this.args.refresh?.();
  }

  get hasViewCount() {
    return this.args.source.viewCount != null;
  }

  get hasLikeCount() {
    return this.args.source.likeCount != null;
  }

  get hasCommentCount() {
    return this.args.source.commentCount != null;
  }

  get hasStats() {
    return this.hasViewCount || this.hasLikeCount || this.hasCommentCount;
  }

  @action
  openLightbox(event: MouseEvent): void {
    event.preventDefault();
    const thumb = sourceThumbnailUrl(this.args.source);
    if (thumb) {
      this.lightbox.open(thumb, this.args.source.title || 'Content Source');
    }
  }

  <template>
    {{#if (and (not this.hasCompleted) (isProcessing @source))}}
      <SourceProcessing
        @source={{@source}}
        @onCompleted={{this.handleCompleted}}
        @onRefresh={{@refresh}}
      />
    {{else}}
      {{#if (isFailedState @source)}}
        <div class="alert alert-error mb-4" role="alert">
          <Icon @icon={{AlertCircleIcon}} class="h-5 w-5 shrink-0" />
          <div>
            <h3 class="font-bold">Extraction Failed</h3>
            {{#if @source.errorMessage}}
              <div class="text-sm">{{@source.errorMessage}}</div>
            {{/if}}
          </div>
        </div>
      {{/if}}
      <div class="bg-base-100 rounded-box p-6 shadow">
        <div class="flex flex-col gap-6 sm:flex-row">
          {{#let (sourceThumbnailUrl @source) as |thumb|}}
            {{#if thumb}}
              <button
                type="button"
                class="rounded-box mx-auto w-56 shrink-0 self-start overflow-hidden p-0 sm:mx-0
                  {{if
                    (isImageSource @source)
                    'aspect-square'
                    'aspect-[9/16]'
                  }}"
                aria-label="View full size image"
                {{on "click" this.openLightbox}}
              >
                <img
                  {{imageFadeIn}}
                  src={{thumb}}
                  data-fallback-src="/images/placeholders/source.png"
                  alt={{or @source.title "Content Source"}}
                  class="h-full w-full object-cover opacity-0 transition-opacity duration-300"
                  loading="lazy"
                />
              </button>
            {{/if}}
          {{/let}}
          <div class="min-w-0 flex-1">
            <h1 class="mb-2 text-2xl font-bold">
              {{or @source.title "Content Source"}}
            </h1>
            <div class="mb-4 flex flex-wrap items-center gap-3">
              <PlatformBadge @platform={{@source.platform}} />
              {{#if
                (and
                  @source.language
                  (notEq @source.language this.language.preferredLanguage)
                )
              }}
                <span class="badge badge-outline">
                  {{@source.language}}
                </span>
              {{/if}}
              {{#if (and @source.duration (not (isImageSource @source)))}}
                <span class="text-base-content/60 text-sm">
                  {{formatDuration @source.duration}}
                </span>
              {{/if}}
            </div>

            {{#if (isExternalUrl @source.url)}}
              <a
                href={{@source.url}}
                target="_blank"
                rel="noopener noreferrer"
                class="link link-primary"
              >
                View original &nearr;
              </a>
            {{/if}}

            {{#if @source.description}}
              <p class="text-base-content/80 mt-4">
                {{@source.description}}
              </p>
            {{/if}}

            {{! AI-generated summary }}
            {{#let (firstSummary @source.summaries) as |summary|}}
              {{#if summary}}
                <div class="bg-base-200 rounded-box mt-4 p-4">
                  <h3
                    class="mb-2 text-sm font-semibold tracking-wide uppercase opacity-60"
                  >
                    {{#if summary.subjectEmoji}}<span
                        class="mr-1"
                      >{{summary.subjectEmoji}}</span>{{/if}}{{or
                      summary.title
                      "AI Summary"
                    }}
                  </h3>
                  {{#if summary.topics.length}}
                    <div class="mb-3 flex flex-wrap gap-1">
                      {{#each summary.topics as |topic|}}
                        <span class="badge badge-sm badge-primary">
                          {{#if topic.emoji}}{{topic.emoji}}
                          {{/if}}{{topic.label}}
                        </span>
                      {{/each}}
                    </div>
                  {{/if}}
                  <HintedSummary
                    @text={{summary.text}}
                    @mentions={{or @mentions (array)}}
                    @mentionId={{@mentionId}}
                  />
                </div>
              {{/if}}
            {{/let}}

            {{#if this.hasStats}}
              <div
                class="stats stats-vertical bg-base-200 sm:stats-horizontal mt-4 shadow"
              >
                {{#if this.hasViewCount}}
                  <div class="stat">
                    <div class="stat-figure text-primary">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        class="inline-block h-6 w-6 stroke-current"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                        ></path>
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                        ></path>
                      </svg>
                    </div>
                    <div class="stat-title">Views</div>
                    <div class="stat-value text-primary">
                      {{formatNumber
                        @source.viewCount
                        notation="compact"
                        compactDisplay="short"
                      }}
                    </div>
                  </div>
                {{/if}}
                {{#if this.hasLikeCount}}
                  <div class="stat">
                    <div class="stat-figure text-secondary">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        class="inline-block h-6 w-6 stroke-current"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
                        ></path>
                      </svg>
                    </div>
                    <div class="stat-title">Likes</div>
                    <div class="stat-value text-secondary">
                      {{formatNumber
                        @source.likeCount
                        notation="compact"
                        compactDisplay="short"
                      }}
                    </div>
                  </div>
                {{/if}}
                {{#if this.hasCommentCount}}
                  <div class="stat">
                    <div class="stat-figure text-accent">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        class="inline-block h-6 w-6 stroke-current"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                        ></path>
                      </svg>
                    </div>
                    <div class="stat-title">Comments</div>
                    <div class="stat-value text-accent">
                      {{formatNumber
                        @source.commentCount
                        notation="compact"
                        compactDisplay="short"
                      }}
                    </div>
                  </div>
                {{/if}}
              </div>
            {{/if}}

          </div>
        </div>
      </div>
    {{/if}}
  </template>
}

// Helper functions
function firstSummary(summaries: Summary[] | null | undefined): Summary | null {
  if (!summaries || !Array.isArray(summaries) || summaries.length === 0) {
    return null;
  }
  return summaries[0]!;
}

function isProcessing(source: ContentSource): boolean {
  const state = source?.state;
  return typeof state === 'string' && PROCESSING_STATES.has(state);
}

function isFailedState(source: ContentSource): boolean {
  return source?.state === 'failed';
}

function isImageSource(source: ContentSource): boolean {
  return source?.sourceType === 'image' || source?.platform === 'image';
}

/**
 * Returns a browser-accessible thumbnail URL for the source.
 * For uploaded images, derives the URL from imagePath if thumbnailUrl is not set.
 */
function sourceThumbnailUrl(source: ContentSource): string | null {
  if (source.thumbnailUrl) return source.thumbnailUrl;
  if (source.imagePath?.startsWith('uploads/')) {
    return toApiUrl(`/api/artifact/${source.imagePath}`);
  }
  return null;
}

/**
 * Returns true only for real external URLs (not image:// protocol used for local/uploaded images).
 */
function isExternalUrl(url: string | null | undefined): boolean {
  if (!url) return false;
  return !url.startsWith('image://');
}
